import React, { useRef } from "react";
import { Typography, Divider } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import "../JaldiLab.css";
import {
  captionStyle,
  captionWithBorders,
  getTextContentHeading,
  h1style,
  h2style,
  h3style,
  h4style,
  paraStyle,
  referenceHtml,
  referenceTootKit,
  sectionStyle,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import sensoryRoom1 from "../../../assets/Images/Sensory_Room1.jpeg";
import sensoryRoom2 from "../../../assets/Images/Sensory_Room2.png";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import detailQuietRoom from "../../../assets/Images/QuietRoom Images/Detail _Quite_Room.png";
import { FigureDesc71, ImageDesc93 } from '../../../utils/tmp/ImageDescription/QuietRoomID';

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const QuietRoom = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content:
        "This Handbook uses the term “Quiet Room” to refer interchangeably to sensory rooms, calm spaces, and quiet zones.",
    },
  ];

  const OjectiveObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Offer a
        <span style={{ color: "#DC002D" }}>
          {" "}
          safe and calming space where individuals can regulate their sensory
          experiences
        </span>{" "}
        and alleviate feelings of overwhelm or distress and regain a sense of
        calm; and
      </>,
      <>
        Ensure a
        <span style={{ color: "#DC002D" }}>
          {" "}
          more inclusive and supportive environment for everyone
        </span>
        , especially for individuals with sensory sensitivities,
        neuro-developmental conditions, or other needs.
      </>,
    ],
    isOrdered: true,
    isImage: true
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "Quiet Rooms should be provided in close proximity to crowded areas within the Court Complex, including waiting rooms, Courtrooms, etc.",

      "Designated Quiet Rooms must be provided near the Accused waiting areas/lock-ups for the use of the Accused visiting the Court Complex. ",

      "Designated Quiet Rooms must be provided within the VWDC Complex or in close proximity to them.",

      <>
        Designated Quiet Room must be provided in close proximity to support
        services such as counselling or legal aid offices (
        <span
          style={sectionStyle}
          onClick={() =>
            goToSection("legalServicesAuthority", "HeadingLegalService")
          }
        >
          Section 2.8.B
        </span>
        ) for additional assistance for users.
      </>,

      "Quiet Rooms must be placed along accessible routes, accommodating persons with mobility challenges. ",
    ],
    isOrdered: true,
    isImage: true
  };

  const designConsiderationsObject = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      "The Quiet Rooms must be designed to have adjustable sensory elements for sensory control such as lighting, sound, and tactile experiences.",

      "They must be designed with a flexible layout and multipurpose furniture to accommodate various preferences, activities and needs, enhancing their usability.",

      "They must be designed with accessibility features such as wide doorways, low-threshold entrances, etc., to accommodate Persons with Disabilities.",

      <>
        Furnishings and equipment in these rooms must be chosen to provide
        comfort and safety including soft seating, cushions, bean bags, or cosy
        blankets, calming colours, soothing music and noise-cancelling
        headphones, etc. For further details on specific design considerations,
        please refer to{" "}
        <span
          onClick={() => goToSection("vwdc", "vulnerable-witness")}
          style={sectionStyle}
        >
          Section 2.3.A.
        </span>
      </>,

      "Sensory rooms may contain specialised equipment like sensory panels, bubble tubes, fibre optic lights, tactile toys, weighted blankets, or swings that offer various sensory experiences and help in relaxation.",

      "Temperature-controlled systems must be provided to maintain a comfortable and conducive environment for all users. ",

      "Privacy and safety must be ensured to reduce the potential for sensory overload or anxiety.",

      <>
        The rooms must be acoustically treated to be soundproof to keep out
        external noises and disturbances. For further details on acoustics,
        please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          Annexure 5.
        </span>
      </>,

      "The Quiet Rooms must be equipped with safety features such as emergency call buttons or panic alarms for any emergencies. ",
    ],
    isOrdered: true,
    isImage: true
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 id="commonAmenities" style={h2style}>
          <span style={{ fontSize: "24px" }}>3. </span>COMMON AMENITIES
        </h2>
        <h3 id="HeadingQuietRoom" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.6. </span>Quiet Room
        </h3>
        <p style={paraStyle}>
          Quiet rooms, also known as sensory rooms, calm spaces, or quiet zones,
          {reference(referenceData[0], 1)} are specially designed areas
          intentionally created to provide a controlled and supportive
          environment for individuals, particularly those with autism or sensory
          processing difficulties.
        </p>
        <p style={paraStyle}>
          Quiet rooms must be provided at Court Complexes to ensure a calm and
          controlled environment for individuals, including witnesses, Accused,
          or others involved in legal proceedings, who may experience sensory
          issues, anxiety, or stress.
        </p>
        {getTextContentHeading(OjectiveObject)}
        {getTextContentHeading(adjacenciesObject)}
        {getTextContentHeading(designConsiderationsObject)}
        <CaptionWithBorders1
          image={[detailQuietRoom]}
          isZoom={true}
          caption={"Figure 71: Typical floor plan of a quiet room/sensory room"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc71}
        />

        <CaptionWithBorders1
          image={[sensoryRoom1, sensoryRoom2]}
          caption="Image 93: Example of sensory room that may be provided within a quiet room"
          creditLabel="Source"
          imageCredit="Akron Canton Airport, Ohio"
          zoomImgStyle={{ height: "410px" }}
          imageDescription={ImageDesc93}
        />

      </Typography>

      <h2 ref={referencesRef} id="quietRoomRef" style={referenceStyle}>
        References
        <Divider />
      </h2>
      {referenceHtml(referenceData)}
    </div>
  );
};

export default QuietRoom;
